import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { 
  ClipboardCheck, 
  CheckCircle,
  Circle,
  ExternalLink,
  Calendar,
  User,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';

const Onboarding = () => {
  const { user } = useAuth();
  const [checklistItems, setChecklistItems] = useState([]);
  const [completions, setCompletions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [itemsRes, completionsRes] = await Promise.all([
        api.get('/onboarding-checklist'),
        api.get('/my-onboarding-completions')
      ]);
      setChecklistItems(itemsRes.data);
      setCompletions(completionsRes.data);
    } catch (error) {
      console.error('Failed to load onboarding data:', error);
      toast.error('Failed to load onboarding checklist');
    } finally {
      setLoading(false);
    }
  };

  const isCompleted = (itemId) => {
    return completions.some(c => c.item_id === itemId);
  };

  const getCompletionDate = (itemId) => {
    const completion = completions.find(c => c.item_id === itemId);
    return completion?.completed_at;
  };

  const handleToggleComplete = async (itemId) => {
    try {
      if (isCompleted(itemId)) {
        // Uncomplete
        await api.delete(`/onboarding-checklist/${itemId}/complete`);
        setCompletions(prev => prev.filter(c => c.item_id !== itemId));
        toast.success('Item marked as incomplete');
      } else {
        // Complete
        await api.post(`/onboarding-checklist/${itemId}/complete`);
        setCompletions(prev => [...prev, { 
          item_id: itemId, 
          completed_at: new Date().toISOString() 
        }]);
        toast.success('Item completed!');
      }
    } catch (error) {
      console.error('Failed to update completion:', error);
      toast.error('Failed to update item');
    }
  };

  // Group items by section
  const groupedItems = checklistItems.reduce((acc, item) => {
    const section = item.section || 'General';
    if (!acc[section]) acc[section] = [];
    acc[section].push(item);
    return acc;
  }, {});

  const sectionOrder = ['Setup', 'Prepare', 'Trainings', 'Study Assignment'];
  const sortedSections = Object.keys(groupedItems).sort((a, b) => {
    const aIndex = sectionOrder.indexOf(a);
    const bIndex = sectionOrder.indexOf(b);
    if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });

  const totalItems = checklistItems.length;
  const completedItems = completions.length;
  const progressPercent = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  const getSectionIcon = (section) => {
    switch(section) {
      case 'Setup': return '⚙️';
      case 'Prepare': return '📝';
      case 'Trainings': return '🎓';
      case 'Study Assignment': return '📋';
      default: return '📌';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="onboarding-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900 flex items-center gap-3">
            <ClipboardCheck className="w-8 h-8 text-blue-600" />
            Onboarding Checklist
          </h1>
          <p className="text-slate-600 mt-1">
            Complete your onboarding tasks to get started
          </p>
        </div>
      </div>

      {/* Progress Card */}
      <Card className="border-slate-200 shadow-card bg-gradient-to-r from-blue-50 to-white">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <User className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-slate-900">{user?.name}</p>
                <p className="text-sm text-slate-500">{user?.job_role || 'New Employee'}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-blue-600">{progressPercent}%</p>
              <p className="text-sm text-slate-500">{completedItems}/{totalItems} completed</p>
            </div>
          </div>
          <Progress value={progressPercent} className="h-3" />
          {progressPercent === 100 && (
            <div className="mt-4 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <p className="text-emerald-700 font-medium flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Congratulations! You've completed your onboarding checklist!
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Checklist Sections */}
      <div className="space-y-6">
        {sortedSections.map(section => (
          <Card key={section} className="border-slate-200 shadow-card">
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="text-lg font-heading flex items-center gap-2">
                <span className="text-xl">{getSectionIcon(section)}</span>
                {section}
                <Badge variant="outline" className="ml-2">
                  {groupedItems[section].filter(item => isCompleted(item.id)).length}/{groupedItems[section].length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100">
                {[...groupedItems[section]]
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((item) => {
                  const completed = isCompleted(item.id);
                  const completionDate = getCompletionDate(item.id);
                  
                  return (
                    <div 
                      key={item.id}
                      className={`flex items-start gap-4 p-4 hover:bg-slate-50 transition-colors
                        ${completed ? 'bg-emerald-50/50' : ''}
                      `}
                      data-testid={`checklist-item-${item.id}`}
                    >
                      <div className="pt-0.5">
                        <Checkbox 
                          checked={completed}
                          onCheckedChange={() => handleToggleComplete(item.id)}
                          className="w-5 h-5"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`font-medium ${completed ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
                            {item.title}
                          </p>
                          {completed && (
                            <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          )}
                        </div>
                        {item.description && (
                          <p className={`text-sm mt-1 ${completed ? 'text-slate-400' : 'text-slate-600'}`}>
                            {item.description}
                          </p>
                        )}
                        {item.link && (
                          <a 
                            href={item.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 mt-2"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Open Link
                          </a>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        {completed && completionDate ? (
                          <div className="flex items-center gap-1 text-sm text-emerald-600">
                            <Calendar className="w-4 h-4" />
                            {new Date(completionDate).toLocaleDateString()}
                          </div>
                        ) : (
                          <Badge variant="outline" className="text-amber-600 border-amber-200">
                            Pending
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {checklistItems.length === 0 && (
        <Card className="border-slate-200 shadow-card">
          <CardContent className="text-center py-12">
            <ClipboardCheck className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">No onboarding checklist items assigned yet.</p>
            <p className="text-sm text-slate-400 mt-1">Contact your lead or admin to get started.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Onboarding;
